import React from 'react';

function AboutModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-2xl font-bold text-gray-900">About WordBetween</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6 text-gray-700">
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What is WordBetween?</h3>
              <p className="mb-3">
                WordBetween is a semantic intersection word finder that helps you discover words, phrases, and names that represent the shared meaning between multiple concepts. Instead of just finding synonyms, it identifies the semantic overlap between your ideas.
              </p>
              <p>
                Think of it as finding the "word between" your ideas - that perfect term that captures the essence of what you're trying to express.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">How to Use It</h3>
              <ol className="list-decimal list-inside space-y-2">
                <li><strong>Enter 1-6 concepts</strong> - Add words or short phrases that represent your ideas</li>
                <li><strong>Add context (optional)</strong> - Describe what you're naming or the use case</li>
                <li><strong>Choose tones</strong> - Select the style you want (playful, professional, technical, etc.)</li>
                <li><strong>Select output types</strong> - Specify what kind of results you need (domain names, app names, synonyms, etc.)</li>
                <li><strong>Click "Find Words"</strong> - Get AI-powered semantic intersections</li>
              </ol>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Common Use Cases</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">🚀 Product & Business Naming</h4>
                  <p className="text-sm">Find memorable names for products, companies, and services</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">✍️ Creative Writing</h4>
                  <p className="text-sm">Discover poetic words and creative expressions</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">🎯 Marketing & Branding</h4>
                  <p className="text-sm">Create catchy slogans and brandable terms</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">💡 Brainstorming</h4>
                  <p className="text-sm">Generate ideas and explore concept connections</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">🔍 Domain Names</h4>
                  <p className="text-sm">Find available-sounding domain names for your projects</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">📚 Academic Writing</h4>
                  <p className="text-sm">Discover technical terms and precise vocabulary</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Result Categories</h3>
              <ul className="space-y-1 text-sm">
                <li><strong>Simple Synonyms:</strong> Common, everyday words that everyone understands</li>
                <li><strong>Best Matches:</strong> The strongest semantic intersections</li>
                <li><strong>Brandable Names:</strong> Commercial-ready names for products and companies</li>
                <li><strong>Technical Terms:</strong> Industry-specific and specialized vocabulary</li>
                <li><strong>Poetic Words:</strong> Artistic and evocative language</li>
                <li><strong>Unusual Words:</strong> Rare and interesting vocabulary</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Tips for Best Results</h3>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Use specific, descriptive concepts rather than vague terms</li>
                <li>Include context to guide the AI toward your specific use case</li>
                <li>Experiment with different tones and output types</li>
                <li>Try combining unexpected concepts for creative results</li>
                <li>Use the search history to revisit and refine previous searches</li>
              </ul>
            </section>

            <section className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Powered by AI</h3>
              <p className="text-blue-800 text-sm">
                WordBetween uses advanced AI technology to understand semantic relationships and find meaningful connections between concepts. The results are generated using OpenAI's language models, ensuring high-quality, contextually relevant suggestions.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AboutModal;
